import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material';
import {
  SignalWifiOffOutlined as OfflineIcon,
  RefreshOutlined as RefreshIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const OfflineDetector = () => {
  const { t } = useTranslation();
  const [showOfflineModal, setShowOfflineModal] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isReloading, setIsReloading] = useState(false);

  // Handle browser online/offline events
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setShowOfflineModal(false);
    };

    const handleOffline = () => {
      setIsOnline(false);
      setShowOfflineModal(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Handle page reload
  const handleReload = () => {
    setIsReloading(true);
    // Force a hard reload (bypass cache)
    window.location.reload(true);
  };

  // Handle dismiss (only if browser reports online)
  const handleDismiss = () => {
    if (navigator.onLine) {
      setShowOfflineModal(false);
    }
  };

  return (
    <Dialog
      open={showOfflineModal}
      disableEscapeKeyDown
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxWidth: 400,
        },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <OfflineIcon color="error" sx={{ fontSize: 32 }} />
          <Typography variant="h6" component="span">
            {t('offline.title')}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body1" sx={{ mb: 2 }}>
          {isOnline
            ? t('offline.staleConnectionMessage')
            : t('offline.noConnectionMessage')}
        </Typography>

        <Typography variant="body2" color="text.secondary">
          {t('offline.reloadInstructions')}
        </Typography>
      </DialogContent>

      <DialogActions sx={{ padding: 2, paddingTop: 0 }}>
        {isOnline && (
          <Button onClick={handleDismiss} color="inherit">
            {t('offline.dismiss')}
          </Button>
        )}
        <Button
          onClick={handleReload}
          variant="contained"
          startIcon={isReloading ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
          disabled={isReloading}
        >
          {t('offline.reload')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OfflineDetector;
